import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { newsList, newsCategories } from "@/mocks/news";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 5;

export default function NewsList() {
  useDocumentMeta(
    "影视资讯 - 小黄人电影最新动态与幕后花絮 | 小黄人电影",
    "汇总小黄人系列电影的上映动态、幕后花絮、周边资讯与行业观察，第一时间了解小黄人相关的最新消息。"
  );
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("全部");
  const [page, setPage] = useState(1);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return newsList.filter((n) => {
      if (category !== "全部" && n.category !== category) return false;
      if (
        q &&
        !n.title.toLowerCase().includes(q) &&
        !n.summary.toLowerCase().includes(q) &&
        !n.category.toLowerCase().includes(q)
      ) {
        return false;
      }
      return true;
    });
  }, [query, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = isMobile
    ? filtered.slice(0, currentPage * PAGE_SIZE)
    : filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const reset = () => {
    setQuery("");
    setCategory("全部");
    setPage(1);
  };

  const pill = (active: boolean) =>
    `whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition cursor-pointer ${
      active
        ? "bg-primary-500 text-foreground-950"
        : "border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
    }`;

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            影视资讯
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            汇集小黄人系列电影的上映动态、幕后花絮、周边资讯与行业观察，帮你快速了解最新鲜的消息。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-6 md:py-10">
        <div className="rounded-2xl border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center gap-2 rounded-full bg-background-100 px-4 py-2.5">
            <i className="ri-search-line text-foreground-500"></i>
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="搜索资讯标题或内容…"
              className="w-full min-w-0 bg-transparent text-sm text-foreground-950 placeholder:text-foreground-500 outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setPage(1);
                }}
                className="text-foreground-400 hover:text-foreground-700"
                aria-label="清空"
              >
                <i className="ri-close-circle-line"></i>
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <span className="w-12 shrink-0 text-xs font-medium text-foreground-500">分类</span>
            {["全部", ...newsCategories].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={pill(category === item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-sm text-foreground-500">
          共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 条资讯
        </p>

        {filtered.length === 0 ? (
          <div className="mt-6">
            <EmptyState
              icon="ri-newspaper-line"
              title="暂无相关资讯"
              desc="换个关键词或分类试试，也可以重置筛选查看全部内容。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-4">
              {visible.map((item) => (
                <ArticleCard
                  key={item.id}
                  to={`/news/${item.id}`}
                  title={item.title}
                  cover={item.cover}
                  badge={item.category}
                  meta={item.date}
                  summary={item.summary}
                  query={query}
                />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </div>
    </SiteLayout>
  );
}